// Shared state for opening a profile over a feed instead of navigating to it.
//
// Every feed surface that shows a profile icon needs the same three things: the
// target being previewed, a toast for whatever the drawer reports back, and the
// timer bookkeeping to clear it. This packages them so a tab only has to supply
// the parts that actually differ — the target it builds and the actions it
// offers.
//
// It also owns the profile_open and profile_dwell signals, because it is the
// one place that knows when a profile was opened and when it was closed. Doing
// this per-tab would mean four copies of the same timing, and the first one to
// drift would quietly bias the engagement log.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ProfilePeekTarget } from './ProfilePeekDrawer';
import { signalsService, SignalSurface } from '../../../services/SocialFeed/signalsService';

const TOAST_DURATION_MS = 4000;

export function useProfilePeek(surface: SignalSurface) {
  const [peekTarget, setPeekTarget] = useState<ProfilePeekTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedAt = useRef<number | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  // Clear the pending timer on unmount so it cannot fire against a component
  // that is gone.
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const openPeek = useCallback((target: ProfilePeekTarget) => {
    openedAt.current = Date.now();
    setPeekTarget(target);
    signalsService.track({
      signalType: 'profile_open',
      targetUserId: target.profileId,
      surface,
    });
  }, [surface]);

  const closePeek = useCallback(() => {
    // Dwell is measured on close, since how long someone reads a profile says
    // considerably more about interest than the fact they opened it.
    setPeekTarget((current) => {
      if (current && openedAt.current !== null) {
        signalsService.trackDwell('profile_dwell', Date.now() - openedAt.current, {
          targetUserId: current.profileId,
          surface,
        });
      }
      openedAt.current = null;
      return null;
    });
  }, [surface]);

  return {
    peekTarget,
    isPeekOpen: peekTarget !== null,
    openPeek,
    closePeek,
    toast,
    showToast,
  };
}
